<template>
  <div id="app">
    <h1>Happy Belated Treatmas 2019 App</h1>

    <div v-if="!loaded" class="loader">Loading...</div>

    <template v-else>
      <p>
        Since this is a prompt fill challenge with no tagset, it does not have fandom or character metadata. Sorry!
        Last updated at
        <strong>{{ new Date(timestamp).toLocaleString() }} (local time)</strong>. If anything has changed since that time, it is NOT REFLECTED IN THE
        APP.
      </p>
      <div class="scroll-top" @click="scrollToTop">(^)</div>
      <bookmarks :force-expand="expandBookmarks" @toggle="expandBookmarks = !expandBookmarks"></bookmarks>

      <user-lookup></user-lookup>
      <options></options>

      <table class="main">
        <thead>
          <tr>
            <th class="fandom">Fandom</th>
            <th v-if="unlock" class="prompts">Prompts</th>
          </tr>
        </thead>
        <tbody>
          <tr class="overlay" v-if="updating">
            <td>
              <div></div>
            </td>
          </tr>
          <tr
            v-if="options.loadAll || index <= scrollPosition"
            v-for="(fandom, index) in filtered"
            :key="index"
            :class="{ odd: index % 2 !== 0 }"
          >
            <td class="fandom" data-label="Fandom">
              {{ fandom.name }}
              <button class="bookmark" @click="toggle(fandom)">
                <span v-if="hasBookmark(fandom)" class="fas fa-heart"></span>
                <span v-else class="far fa-heart"></span>
              </button>
            </td>

            <td v-if="unlock" class="prompts">
              <button
                class="button-primary"
                v-if="!prompts[fandom.key]"
                @click="getPrompts(fandom.key)"
              >Get Prompts</button>
              <div v-if="prompts[fandom.key] === 'loading'">Loading...</div>
              <template
                v-if="
                  prompts[fandom.key] &&
                    prompts[fandom.key].length &&
                    prompts[fandom.key] !== 'loading'
                "
              >
                <a
                  href="javascript:void(0);"
                  @click="collapse(prompts[fandom.key].length, $event)"
                >Collapse</a>
                <Prompts :prompts="prompts[fandom.key]"></Prompts>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import Bookmarks from "./components/bookmarks.vue";
import Prompts from "./components/table/prompts.vue";
import Options from "./components/options.vue";
import UserLookup from "./components/user-lookup.vue";

// third party
import _ from "lodash";
import db from "./db.js";
import { mapGetters } from "vuex";

// internal
import utils from "./components/utils.js";

// Remove english articles from fandom names
function removeArticlesCompare(o) {
  if (!o || !o.name) {
    return o;
  }
  const regex = /^(the\s|a\s|an\s)/i;
  return o.name.toLowerCase().replace(regex, "");
}

export default {
  name: "app",
  components: {
    Bookmarks,
    Options,
    Prompts,
    UserLookup
  },
  beforeMount() {
    db.ref("/timestamp")
      .once("value")
      .then(res => {
        this.timestamp = res.val().timestamp;
      });
    this.$store.commit("setOptions", {
      filter: {
        category: "",
        term: ""
      },
      onlyBookmarks: false,
      onlyPrompts: true,
      destyle: false,
      hideCharacters: true
    });

    const bookmarksJson = this.$localStorage.get("belatedbookmarks");
    if (bookmarksJson) {
      this.$store.commit("setBookmarks", JSON.parse(bookmarksJson));
    }

    const promptmarksJson = this.$localStorage.get("belatedpromptmarks");
    if (promptmarksJson) {
      this.$store.commit("setPromptmarks", JSON.parse(promptmarksJson));
    }
  },
  created() {
    window.addEventListener("scroll", this.lazyload);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.lazyload);
  },
  data() {
    return {
      maintenance: false,
      timestamp: null,
      showEggHelp: false,
      showHelp: false,
      expandBookmarks: false,
      down: {},
      mods: false,
      scrollPosition: 100,
      filtered: [],
      updating: true,
      sticky: false,
      promptStates: {}
    };
  },
  computed: {
    ...mapGetters([
      "fandoms",
      "loaded",
      // "hasPrompts",
      "bookmarks",
      // "characters",
      // "categories",
      "promptmarks",
      "unlock",
      "options",
      "user",
      "prompts",
      // "loadedChars",
      // "loadedHasPrompts",
      "loadAll"
    ])
  },
  watch: {
    letters: {
      deep: true,
      handler(val) {
        if (!Object.keys(val).length) {
          return;
        }
        const scrubbed = this.scrubLettermarks(this.lettermarks, val);
        this.$store.commit("setLettermarks", scrubbed);
        this.$localStorage.set("belatedlettermarks", JSON.stringify(scrubbed));
      }
    },
    options: {
      deep: true,
      handler(val) {
        this.updating = true;
        if (
          (val.loadAll || val.onlyLetters || val.onlyBookmarks) &&
          !this.loadAll.fandoms
        ) {
          this.$store.commit("loadAllFandoms", true);
          this.scrollPosition = this.fandoms.length;
          this.updateFilter();
        } else {
          this.updateFilter();
        }
      }
    },
    loaded() {
      this.updateFilter();
    },
    scrollPosition(val) {
      if (val >= this.fandoms.length) {
        return;
      }
      this.updateFilter();
    }
  },
  methods: {
    ...utils,
    updateFilter() {
      this.updating = true;
      if (
        !this.options.onlyPrompts &&
        !this.options.filter.term.length &&
        !this.options.onlyBookmarks
      ) {
        this.filtered = _.take(
          _.sortBy(this.fandoms, [removeArticlesCompare]),
          this.scrollPosition
        );
        setTimeout(() => {
          this.updating = false;
        }, 200);
        return;
      }

      let arr = _.filter(this.fandoms, f => {
        return f.name.length;
      });

      if (this.options.onlyBookmarks) {
        const bookmarkedFandoms = [];
        _.each(this.bookmarks, b => {
          bookmarkedFandoms.push(b.name);
        });

        arr = _.filter(arr, o => {
          return _.includes(bookmarkedFandoms, o);
        });
      }

      if (this.options.filter.term.length) {
        arr = _.filter(arr, o => {
          return (
            o.name
              .toLowerCase()
              .indexOf(this.options.filter.term.toLowerCase()) > -1
          );
        });
      }

      // If filtering by term or category, preload everything if there are more than a few
      // Also sort by alpha name instead of alpha category when there is a search term
      if (this.options.filter.term.length && arr.length > 5) {
        this.scrollPosition = this.fandoms.length;
        this.filtered = _.sortBy(arr, [removeArticlesCompare]);
        setTimeout(() => {
          this.updating = false;
        }, 200);
        // Otherwise, just take the i/o hit
      } else if (this.options.filter.term.length) {
        this.filtered = _.take(
          _.sortBy(arr, [removeArticlesCompare]),
          this.scrollPosition
        );
        setTimeout(() => {
          this.updating = false;
        }, 200);
      } else {
        this.filtered = _.take(
          _.sortBy(arr, [removeArticlesCompare]),
          this.scrollPosition
        );
        setTimeout(() => {
          this.updating = false;
        }, 200);
      }
    },
    lazyload() {
      const y = window.scrollY;
      const totalHeight = document.body.scrollHeight;
      if (window.pageYOffset > 100) {
        this.sticky = true;
      } else {
        this.sticky = false;
      }
      if (this.loadAll.fandoms) {
        return;
      }
      if (
        totalHeight -
          y -
          (document.documentElement.scrollTop || document.body.scrollTop) <
        50
      ) {
        if (this.scrollPosition < this.fandoms.length) {
          const newPosition = this.scrollPosition + 100;

          this.scrollPosition = newPosition;
        }
      }
    },
    collapse(length, e) {
      e.target.innerText =
        e.target.innerText !== "Collapse" ? "Collapse" : `Expand (${length})`;
      e.target.nextElementSibling.classList.toggle("hide");
    },

    // utilities
    scrollToTop() {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    // scrub bookmarks in case letters have changed
    scrubLettermarks(lettermarks, letters) {
      return _.filter(lettermarks, l => {
        const fandom = letters[l.key]; //[l.username];
        return l && fandom && fandom[l.username];
      });
    }
  }
};
</script>

<style lang="scss">
@import "./styles/app.scss";
@import "./styles/mobile.scss";
</style>
